import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import {
  AlertTriangle, Plus, Upload, Camera, ImagePlus, X, Eye, Trash2, Pencil,
  Loader2, Wrench, Mail, MapPin, Calendar as CalendarIcon, ShieldAlert,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["super_admin", "owner_director", "operations_admin"]);

const SEVERITY = [
  { key: "low", color: "#34D399", bg: "rgba(52,211,153,0.12)", border: "rgba(52,211,153,0.4)" },
  { key: "medium", color: "#FBBF24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.4)" },
  { key: "high", color: "#FB923C", bg: "rgba(251,146,60,0.12)", border: "rgba(251,146,60,0.4)" },
  { key: "critical", color: "#EF4444", bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)" },
];

const STATUSES = ["open", "quoted", "in_progress", "repaired", "closed"];

export default function DamageReports() {
  const t = useT();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [reports, setReports] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [orderingDoc, setOrderingDoc] = useState(null);
  const [filterAsset, setFilterAsset] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const reload = async () => {
    const params = {};
    if (filterAsset) params.asset_id = filterAsset;
    if (filterStatus) params.status = filterStatus;
    const [r, a] = await Promise.all([
      api.get("/damage-reports", { params }),
      api.get("/assets"),
    ]);
    setReports(r.data); setAssets(a.data); setLoading(false);
  };
  useEffect(() => { reload(); }, [filterAsset, filterStatus]);

  const handleDelete = async (rep) => {
    if (!window.confirm(t("damage.deleteConfirm"))) return;
    try {
      await api.delete(`/damage-reports/${rep.id}`);
      toast.success(t("damage.deletedToast"));
      setDetail(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="damage-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#EF4444] flex items-center gap-2">
            <ShieldAlert className="w-3 h-3" />
            {t("damage.subtitle")}
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("damage.title")}</h2>
          <p className="text-xs text-[#64748B] mt-1 max-w-2xl">{t("damage.intro")}</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowDialog(true); }} data-testid="new-damage-btn" className="bg-[#EF4444] hover:bg-[#DC2626]">
          <Plus className="w-4 h-4 mr-1" /> {t("damage.new")}
        </Button>
      </div>

      <div className="ledger-card p-3 flex items-center gap-2 flex-wrap" data-testid="damage-filters">
        <span className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.filter.asset")}:</span>
        <select value={filterAsset} onChange={(e) => setFilterAsset(e.target.value)} className="bg-[#050A14] border border-[#1E293B] rounded-md px-2 py-1 text-xs">
          <option value="">{t("common.all")}</option>
          {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <span className="text-[10px] font-mono tracking-widest text-[#94A3B8] ml-2">{t("damage.filter.status")}:</span>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#050A14] border border-[#1E293B] rounded-md px-2 py-1 text-xs">
          <option value="">{t("common.all")}</option>
          {STATUSES.map(s => <option key={s} value={s}>{t(`damage.status.${s}`)}</option>)}
        </select>
      </div>

      {loading ? <div className="h-64 ledger-card animate-pulse" /> :
        reports.length === 0 ? (
          <div className="ledger-card p-12 text-center text-[#94A3B8]" data-testid="damage-empty">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <div className="text-sm">{t("damage.empty")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map(r => {
              const sev = SEVERITY.find(s => s.key === r.severity) || SEVERITY[1];
              const heroPid = (r.photo_ids || [])[0];
              const asset = assets.find(a => a.id === r.asset_id);
              return (
                <button key={r.id} onClick={() => setDetail(r)} data-testid={`damage-card-${r.id}`}
                  className="ledger-card text-left overflow-hidden hover:border-[#EF4444]/40 transition-colors p-0">
                  <div className="relative aspect-video bg-[#050A14] overflow-hidden">
                    {heroPid ? (
                      <img src={`${API_BASE}/media/${heroPid}/download`} alt={r.title}
                        className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#475569]">
                        <AlertTriangle className="w-10 h-10" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-widest"
                         style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}>
                      {t(`damage.severity.${r.severity}`).toUpperCase()}
                    </div>
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-mono tracking-widest bg-[#050A14]/80 text-[#94A3B8] border border-[#1E293B]">
                      {t(`damage.status.${r.status}`).toUpperCase()}
                    </div>
                    {(r.photo_ids?.length || 0) > 1 && (
                      <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md text-[10px] font-mono bg-black/60 text-white">
                        +{r.photo_ids.length - 1}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="font-medium line-clamp-2">{r.title}</div>
                    <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-2 flex-wrap">
                      <span>{asset?.name || "—"}</span>
                      {r.location && <><span>·</span><MapPin className="w-3 h-3" /><span className="line-clamp-1">{r.location}</span></>}
                    </div>
                    <div className="text-[10px] font-mono text-[#64748B] mt-2 flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" /> {fmtDate(r.date_observed)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      }

      {showDialog && (
        <DamageDialog
          assets={assets}
          editing={editing}
          onClose={() => { setShowDialog(false); setEditing(null); }}
          onSaved={() => { setShowDialog(false); setEditing(null); reload(); }}
        />
      )}

      {detail && (
        <DamageDetail
          report={detail}
          asset={assets.find(a => a.id === detail.asset_id)}
          isAdmin={isAdmin}
          onClose={() => setDetail(null)}
          onEdit={() => { setEditing(detail); setDetail(null); setShowDialog(true); }}
          onDelete={() => handleDelete(detail)}
          onOrderRepair={() => { setOrderingDoc(detail); setDetail(null); }}
        />
      )}

      {orderingDoc && (
        <OrderRepairDialog
          report={orderingDoc}
          asset={assets.find(a => a.id === orderingDoc.asset_id)}
          onClose={() => setOrderingDoc(null)}
          onDone={() => { setOrderingDoc(null); reload(); }}
        />
      )}
    </div>
  );
}

// ---------------- New/Edit dialog ----------------
function DamageDialog({ assets, editing, onClose, onSaved }) {
  const t = useT();
  const isEdit = !!editing;
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    asset_id: editing?.asset_id || assets[0]?.id || "",
    title: editing?.title || "",
    severity: editing?.severity || "medium",
    location: editing?.location || "",
    date_observed: editing?.date_observed || today,
    description: editing?.description || "",
  });
  const [existingPhotoIds, setExistingPhotoIds] = useState(editing?.photo_ids || []);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const inputRef = useRef();
  const cameraRef = useRef();

  const totalPhotos = existingPhotoIds.length + newFiles.length;

  const handlePickFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(f => f.type?.startsWith("image/") || /\.(heic|heif|jpg|jpeg|png|webp)$/i.test(f.name));
    if (arr.length === 0) return;
    setNewFiles(prev => [...prev, ...arr]);
  };

  const removeExisting = (fid) => setExistingPhotoIds(prev => prev.filter(p => p !== fid));
  const removeStaged = (idx) => setNewFiles(prev => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("damage.dialog.titleRequired")); return; }
    if (!form.description.trim()) { toast.error(t("damage.dialog.descRequired")); return; }
    if (!form.asset_id) { toast.error(t("damage.dialog.assetRequired")); return; }
    if (totalPhotos === 0 && !isEdit) { toast.error(t("damage.dialog.photosRequired")); return; }

    setSaving(true);
    try {
      const uploadedIds = [];
      for (const f of newFiles) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("linked_type", "damage_report");
        if (editing?.id) fd.append("linked_id", editing.id);
        const { data } = await api.post("/media/upload", fd);
        uploadedIds.push(data.id);
      }
      const photo_ids = [...existingPhotoIds, ...uploadedIds];
      if (isEdit) {
        await api.patch(`/damage-reports/${editing.id}`, { ...form, photo_ids });
        toast.success(t("damage.updatedToast"));
      } else {
        await api.post("/damage-reports", { ...form, photo_ids });
        toast.success(t("damage.savedToast"));
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-2xl" data-testid="damage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
            {isEdit ? t("damage.dialog.editTitle") : t("damage.dialog.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.assetLabel")}</Label>
            <select value={form.asset_id} onChange={(e) => setForm(f => ({...f, asset_id: e.target.value}))}
              data-testid="damage-asset-select"
              className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.titleLabel")}</Label>
            <Input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))}
              data-testid="damage-title-input" placeholder={t("damage.dialog.titlePlaceholder")}
              className="bg-[#050A14] border-[#1E293B] mt-1" />
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.severityLabel")}</Label>
            <div className="mt-1 grid grid-cols-4 gap-2" data-testid="damage-severity-picker">
              {SEVERITY.map(s => (
                <button key={s.key} type="button" onClick={() => setForm(f => ({...f, severity: s.key}))}
                  data-testid={`damage-severity-${s.key}`}
                  className={`px-2 py-2 rounded-md border text-[11px] font-mono tracking-widest uppercase transition-all ${form.severity === s.key ? "ring-2" : ""}`}
                  style={{
                    background: form.severity === s.key ? s.bg : "transparent",
                    borderColor: form.severity === s.key ? s.border : "#1E293B",
                    color: form.severity === s.key ? s.color : "#94A3B8",
                    boxShadow: form.severity === s.key ? `inset 0 0 0 1px ${s.border}` : "none",
                  }}>
                  {t(`damage.severity.${s.key}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.location")}</Label>
              <Input value={form.location} onChange={(e) => setForm(f => ({...f, location: e.target.value}))}
                data-testid="damage-location-input"
                placeholder={t("damage.dialog.locationPlaceholder")}
                className="bg-[#050A14] border-[#1E293B] mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.dateObserved")}</Label>
              <Input type="date" value={form.date_observed} onChange={(e) => setForm(f => ({...f, date_observed: e.target.value}))}
                data-testid="damage-date-input" className="bg-[#050A14] border-[#1E293B] mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.description")}</Label>
            <textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))}
              rows={5} data-testid="damage-description-input"
              placeholder={t("damage.dialog.descriptionPlaceholder")}
              className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm resize-none" />
          </div>

          {/* Photos */}
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8] flex items-center justify-between">
              <span>{t("damage.dialog.photos")}</span>
              <span className="text-[#EF4444]">{totalPhotos} {t("damage.dialog.photoCount")}</span>
            </Label>

            {(existingPhotoIds.length > 0 || newFiles.length > 0) && (
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="damage-photos-list">
                {existingPhotoIds.map((fid, idx) => (
                  <div key={fid} className="relative group bg-[#050A14] border border-[#1E293B] rounded-md overflow-hidden aspect-video" data-testid={`damage-existing-photo-${idx}`}>
                    <img src={`${API_BASE}/media/${fid}/download`} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <button onClick={() => removeExisting(fid)} type="button"
                      className="absolute top-1 right-1 p-1 rounded-md bg-[#EF4444] text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {newFiles.map((f, idx) => (
                  <StagedPhotoThumb key={`${f.name}-${idx}`} file={f} onRemove={() => removeStaged(idx)} testid={`damage-staged-photo-${idx}`} />
                ))}
              </div>
            )}

            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDropActive(true); }}
              onDragLeave={() => setDropActive(false)}
              onDrop={(e) => { e.preventDefault(); setDropActive(false); handlePickFiles(e.dataTransfer?.files); }}
              data-testid="damage-photo-dropzone"
              className={`mt-2 border-2 border-dashed rounded-md p-5 text-center cursor-pointer transition-colors ${dropActive ? "border-[#EF4444] bg-[#EF4444]/10" : "border-[#1E293B] hover:border-[#334155]"}`}
            >
              <Upload className="w-5 h-5 mx-auto mb-1 text-[#94A3B8]" />
              <div className="text-xs text-[#94A3B8]">{t("damage.dialog.dropHint")}</div>
              <input ref={inputRef} type="file" multiple accept="image/*,.heic,.heif"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={(e) => handlePickFiles(e.target.files)} data-testid="damage-photo-input" />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
                onChange={(e) => handlePickFiles(e.target.files)} data-testid="damage-camera-input" />
            </div>
            <div className="mt-2 flex items-center gap-4 flex-wrap">
              <button type="button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                data-testid="damage-photo-gallery-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC]">
                <ImagePlus className="w-3.5 h-3.5" /> {t("damage.dialog.addPhoto")}
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                data-testid="damage-photo-camera-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC]">
                <Camera className="w-3.5 h-3.5" /> {t("assets.fields.takePhoto")}
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="damage-save-btn" className="bg-[#EF4444] hover:bg-[#DC2626]">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {saving ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StagedPhotoThumb({ file, onRemove, testid }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (file.type?.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);
  return (
    <div className="relative group bg-[#050A14] border border-[#EF4444]/40 rounded-md overflow-hidden aspect-video" data-testid={testid}>
      {src ? <img src={src} alt={file.name} className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-[10px] font-mono text-[#94A3B8] p-2">{file.name}</div>}
      <button onClick={onRemove} type="button"
        className="absolute top-1 right-1 p-1 rounded-md bg-[#EF4444] text-white opacity-0 group-hover:opacity-100 transition-opacity">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// ---------------- Detail dialog ----------------
function DamageDetail({ report, asset, isAdmin, onClose, onEdit, onDelete, onOrderRepair }) {
  const t = useT();
  const sev = SEVERITY.find(s => s.key === report.severity) || SEVERITY[1];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-3xl" data-testid="damage-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight pr-8">{report.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono tracking-widest uppercase"
              style={{ background: sev.bg, color: sev.color, border: `1px solid ${sev.border}` }}>
              {t(`damage.severity.${report.severity}`)}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono tracking-widest uppercase bg-[#050A14] text-[#94A3B8] border border-[#1E293B]">
              {t(`damage.status.${report.status}`)}
            </span>
            <span className="text-[11px] text-[#94A3B8] ml-2">{asset?.name}</span>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            {report.location && (
              <div>
                <div className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.location")}</div>
                <div className="mt-1">{report.location}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.dateObserved")}</div>
              <div className="mt-1 font-mono text-xs">{fmtDate(report.date_observed)}</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.description")}</div>
            <div className="mt-2 p-3 bg-[#050A14] border-l-2 border-[#EF4444] rounded-r-md text-sm whitespace-pre-wrap">{report.description}</div>
          </div>

          {(report.photo_ids || []).length > 0 && (
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[#94A3B8] mb-2">{t("damage.dialog.photos")} ({report.photo_ids.length})</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="damage-detail-photos">
                {report.photo_ids.map((fid, idx) => (
                  <a key={fid} href={`${API_BASE}/media/${fid}/download`} target="_blank" rel="noopener noreferrer"
                    className="bg-[#050A14] border border-[#1E293B] rounded-md overflow-hidden aspect-video block hover:border-[#EF4444]/40">
                    <img src={`${API_BASE}/media/${fid}/download`} alt={`photo ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}

          {report.linked_ticket_id && (
            <div className="ledger-card !bg-[#050A14] p-3 text-xs flex items-center gap-2" data-testid="damage-linked-ticket">
              <Wrench className="w-4 h-4 text-[#34D399]" />
              <span className="text-[#94A3B8]">{t("damage.linkedTicket")}</span>
              <code className="font-mono text-[#34D399]">{report.linked_ticket_id.slice(0, 8)}…</code>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={onDelete} data-testid="damage-detail-delete-btn" className="bg-transparent border-[#EF4444]/40 text-[#F87171] hover:bg-[#EF4444]/10">
              <Trash2 className="w-4 h-4 mr-1" /> {t("common.delete")}
            </Button>
          )}
          <Button variant="outline" onClick={onEdit} data-testid="damage-detail-edit-btn" className="bg-transparent border-[#1E293B]">
            <Pencil className="w-4 h-4 mr-1" /> {t("common.edit")}
          </Button>
          <Button onClick={onOrderRepair} data-testid="damage-detail-order-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            <Wrench className="w-4 h-4 mr-1" /> {t("damage.orderRepair")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Order Repair dialog ----------------
function OrderRepairDialog({ report, asset, onClose, onDone }) {
  const t = useT();
  const defaultSubject = `LaSovita Ledger — Damage report: ${report.title}`;
  const [toEmail, setToEmail] = useState("");
  const [providerPhone, setProviderPhone] = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("");
  const [createTicket, setCreateTicket] = useState(true);
  const [ticketSeverity, setTicketSeverity] = useState(report.severity);
  const [ticketScheduledFor, setTicketScheduledFor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!toEmail && !createTicket) {
      toast.error(t("damage.order.nothingToDo"));
      return;
    }
    if (toEmail && !/^\S+@\S+\.\S+$/.test(toEmail)) {
      toast.error(t("documents.email.invalidEmail"));
      return;
    }
    setSubmitting(true);
    try {
      const body = { create_ticket: createTicket };
      if (toEmail) {
        body.to_email = toEmail;
        body.subject = subject || undefined;
        body.message = message || undefined;
      }
      if (createTicket) {
        body.ticket_severity = ticketSeverity;
        body.ticket_scheduled_for = ticketScheduledFor || undefined;
        if (providerPhone) body.provider_phone = providerPhone;
      }
      const { data } = await api.post(`/damage-reports/${report.id}/order-repair`, body);
      const parts = [];
      if (data.actions?.emailed) parts.push(t("damage.order.toastEmailed"));
      if (data.actions?.ticket_created) parts.push(t("damage.order.toastTicket"));
      toast.success(parts.join(" + ") || t("damage.order.toastDone"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-lg" data-testid="order-repair-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#007AFF]" />
            {t("damage.order.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="ledger-card !bg-[#050A14] p-3 text-xs space-y-0.5" data-testid="order-repair-summary">
            <div className="font-medium text-sm">{report.title}</div>
            <div className="text-[#94A3B8]">{asset?.name} · {t(`damage.severity.${report.severity}`)}</div>
            <div className="font-mono text-[10px] text-[#4DA6FF] mt-1">{(report.photo_ids || []).length} {t("damage.dialog.photoCount")}</div>
          </div>

          {/* Email section */}
          <div className="ledger-card !bg-[#0B1221] p-3 space-y-2 border-[#1E293B]" data-testid="order-repair-email-block">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#34D399]" />
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("damage.order.emailHeader")}</span>
            </div>
            <Input type="email" placeholder="workshop@example.com" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
              data-testid="order-repair-to-input" className="bg-[#050A14] border-[#1E293B]" />
            {toEmail && (
              <>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                  data-testid="order-repair-subject-input" className="bg-[#050A14] border-[#1E293B]" />
                <textarea rows={3} placeholder={t("damage.order.messagePlaceholder")} value={message} onChange={(e) => setMessage(e.target.value)}
                  data-testid="order-repair-message-input"
                  className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm resize-none" />
              </>
            )}
          </div>

          {/* Ticket section */}
          <label className="ledger-card !bg-[#0B1221] p-3 space-y-2 border-[#1E293B] cursor-pointer block" data-testid="order-repair-ticket-block">
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={createTicket} onChange={(e) => setCreateTicket(e.target.checked)}
                data-testid="order-repair-ticket-checkbox" className="w-4 h-4 accent-[#007AFF]" />
              <Wrench className="w-4 h-4 text-[#007AFF]" />
              <span className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("damage.order.ticketHeader")}</span>
            </div>
            {createTicket && (
              <div className="space-y-2 pl-6" onClick={(e) => e.preventDefault()}>
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.dialog.severityLabel")}</Label>
                  <select value={ticketSeverity} onChange={(e) => setTicketSeverity(e.target.value)}
                    data-testid="order-repair-ticket-severity"
                    className="mt-1 w-full bg-[#050A14] border border-[#1E293B] rounded-md px-2 py-1.5 text-xs">
                    {SEVERITY.map(s => <option key={s.key} value={s.key}>{t(`damage.severity.${s.key}`)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.order.scheduledFor")}</Label>
                  <Input type="date" value={ticketScheduledFor} onChange={(e) => setTicketScheduledFor(e.target.value)}
                    data-testid="order-repair-ticket-date" className="bg-[#050A14] border-[#1E293B] mt-1" />
                </div>
                {toEmail && (
                  <div>
                    <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("damage.order.providerPhone")}</Label>
                    <Input value={providerPhone} onChange={(e) => setProviderPhone(e.target.value)}
                      placeholder="+386 ..." data-testid="order-repair-ticket-phone" className="bg-[#050A14] border-[#1E293B] mt-1" />
                  </div>
                )}
              </div>
            )}
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="order-repair-submit-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
            {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {submitting ? t("common.saving") : t("damage.order.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
